if (!customElements.get('po-moco-selector')) {
  /**
   * MOCO (model + color) thumbnail strip on the product page.
   * Liquid renders the first card + skeletons; this element fills the rest after
   * `variantsHandleGraphLoaded` delivers related products from the Storefront API.
   */
  class PoMocoSelector extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currentMoco = this.querySelector('.js-moco-element.js-selected-moco');
      this.galleryHover = document.querySelector('[data-moco-gallery-hover]');

      this.hoverTimer = null;

      this.productDom = {
        frame: this.querySelector('[data-moco-frame-hover]'),
        lens: this.querySelector('[data-moco-lens-hover]'),
        polarizedBadge: this.querySelector('.js-polarized-badge'),
      }

      const { frame, lens } = this.productDom;
      if (frame) frame.classList.add('tw-transition-opacity');
      if (lens) lens.classList.add('tw-transition-opacity');

      this.onPointerEnter = (evt) => {
        const moco = evt.target.closest('.js-moco-element');
        if (!moco || moco === this.currentMoco) return;
        this.highlightMoco(evt, moco);
      };

      this.onPointerLeave = (evt) => {
        const moco = evt.target.closest('.js-moco-element');
        if (!moco || moco === this.currentMoco) return;
        this.highlightMoco(evt, this.currentMoco);
      };

      // Capture phase so hovering nested content still bubbles as one MOCO cell.
      this.addEventListener('mouseenter', this.onPointerEnter, true);
      this.addEventListener('mouseleave', this.onPointerLeave, true);

      this.currentHandle = this.dataset.currentHandle;
      this.mocoGrid = this.querySelector('[data-moco-grid]');
      this.mocoTemplate = this.querySelector('[data-moco-template]');
      this.mocoHoverTemplate = this.querySelector('[data-moco-hover-template]');
      this.overflowBtn = this.querySelector('[data-overflow-btn]');

      // Staggered reveal: `poMocoFadeIn` keyframes live in `po-product-moco-picker.liquid`.
      this.animationRevealEnabled = true;

      if (window.variantsHandleGraphData?.loaded) {
        this.renderMocos(window.variantsHandleGraphData.products || []);
      }

      window.addEventListener('variantsHandleGraphLoaded', (event) => {
        this.renderMocos((event.detail && event.detail.products) || []);
      });
    }

    /**
     * Renders MOCO elements into the grid from Storefront API product data.
     * Appends cloned MOCO items after the server-rendered one, then activates
     * the overflow button when items exceed the visible grid slots.
     * @param {Object[]} products - Product objects from the Storefront API.
     */
    renderMocos(products) {
      this.removeSkeletons();
      if (!this.mocoGrid || !this.mocoTemplate || !products.length) return;

      const mocos = products.filter(p => p.handle !== this.currentHandle);
      // Total variants: Liquid-rendered selected card + API siblings (always excludes current handle).
      const total = mocos.length + 1;
      const needsOverflow = total > 5;
      // Cap at 4 extra thumbnails: the grid only shows that many before "+N" (see Liquid breakpoints).
      const renderCount = total > 6 ? 4 : mocos.length;

      for (let i = 0; i < renderCount; i++) {
        this.mocoGrid.appendChild(this.buildMocoElement(mocos[i], i + 1, total));
      }

      if (needsOverflow) {
        // Mobile/desktop grids show max 3 mocos + overflow (5 cols - 1 selected - 1 overflow)
        const visibleCount = Math.min(renderCount, 3);
        this.showOverflowButton(total, visibleCount);
      }
    }

    /**
     * Removes all skeleton placeholder elements from the MOCO grid.
     */
    removeSkeletons() {
      this.querySelectorAll('[data-moco-skeleton]').forEach(el => el.remove());
    }

    /**
     * Applies the staggered fade-in CSS animation to an element.
     * Requires the poMocoFadeIn keyframes defined in the Liquid template.
     * @param {HTMLElement} el - Target element.
     * @param {number} position - Sequential index (0-based) for the stagger delay.
     */
    applyRevealAnimation(el, position) {
      if (!this.animationRevealEnabled) return;
      // `both` = delay holds opacity:0, end state keeps opacity:1 (no class toggles).
      el.style.animation = `poMocoFadeIn 300ms ease ${position * 100}ms both`;
    }

    /**
     * Activates the pre-rendered overflow button and moves it to the end
     * of the grid. Sets the responsive count labels and visibility classes
     * so the correct "+N" value is shown at each breakpoint.
     * @param {number} total - Total MOCO count including the server-rendered item.
     * @param {number} delayIdx - Position index used to compute the fade-in delay.
     */
    showOverflowButton(total, delayIdx) {
      if (!this.overflowBtn) return;

      this.overflowBtn.classList.remove('tw-hidden');
      // Fade-in sequentially after the last visible MOCO element
      this.applyRevealAnimation(this.overflowBtn, delayIdx);

      // total > 6: button visible on all breakpoints; counts differ by column layout in CSS.
      if (total > 6) {
        this.overflowBtn.classList.add('tw-flex');
        const tabletSpan = this.overflowBtn.querySelector('[data-overflow-count-tablet]');
        if (tabletSpan) tabletSpan.textContent = `+${total - 5}`;
      } else {
        // Only 6 variants: overflow only where the 5-col layout needs it (hidden on tablet).
        this.overflowBtn.classList.add('tw-flex', 'tb:tw-hidden', 'sd:tw-flex');
      }

      const defaultSpan = this.overflowBtn.querySelector('[data-overflow-count-default]');
      if (defaultSpan) defaultSpan.textContent = `+${total - 4}`;

      this.mocoGrid.appendChild(this.overflowBtn);
    }

    /**
     * Clones the MOCO template and populates it with product data.
     * @param {Object} product - Product object from the Storefront API.
     * @param {number} idx - 1-based index among API-rendered cards (Liquid first card is not in this loop).
     * @param {number} total - Total MOCO count including the Liquid-rendered first item.
     * @returns {DocumentFragment}
     */
    buildMocoElement(product, idx, total) {
      const clone = this.mocoTemplate.content.cloneNode(true);
      const anchor = clone.querySelector('a');

      const color = product.color?.value || '';
      const lens = product.lensColorDescription?.value || '';
      const polarized = product.polarized ? product.polarized.value.toLowerCase() : 'false';
      const mocoAlt = (anchor.dataset.mocoAltBase || '')
        .replace('{frame}', color)
        .replace('{lenses}', lens);

      anchor.href = product.url;
      anchor.dataset.mocoProductHandle = product.handle;
      if (color) anchor.dataset.frame = color;
      if (lens) anchor.dataset.lens = lens;
      anchor.dataset.polarized = polarized;

      // Staggered fade-in: on hidden elements (display:none) the animation is inert,
      // on tablet where they become visible (tb:tw-flex) it plays correctly
      this.applyRevealAnimation(anchor, idx - 1);

      if (idx >= 4 && total > 5) {
        // 4th API slot: fits tablet 6-col row; hidden on mobile + desktop wide where only 3 show + overflow.
        anchor.classList.add('tw-hidden', 'tb:tw-flex', 'sd:tw-hidden');
      }

      const isAccessory = (product.productType || '').toLowerCase() === 'accessories';
      const swatchImg = product.colorswatchImage?.reference?.image;
      const rawThumbSrc = swatchImg?.url || product.featuredImage?.url || '';
      const thumbSrc = isAccessory
        ? this.imageUrl(rawThumbSrc, { width: 400 })
        : this.imageUrl(rawThumbSrc, { width: 400, height: 400, crop: 'left' });
      const thumb = clone.querySelector('[data-moco-thumb]');

      if (thumb && thumbSrc) {
        thumb.src = thumbSrc;
        thumb.alt = mocoAlt;
        if (isAccessory) thumb.classList.replace('tw-object-cover', 'tw-object-contain');
      }

      if (!product.availableForSale) {
        clone.querySelector('[data-moco-thumb-wrapper]')?.classList.add('tw-opacity-50');
      }

      const hoverSrc = this.imageUrl(product.featuredImage?.url || '', { width: 2000 });
      if (hoverSrc) {
        anchor.dataset.hoverSrc = hoverSrc;
        anchor.dataset.hoverAlt = mocoAlt; 
      }

      return clone;
    }

    /**
     * Appends width, height, and crop query parameters to a Shopify CDN image URL.
     * @param {string} url - Shopify CDN image URL.
     * @param {Object} [options]
     * @param {number} [options.width] - Desired image width in pixels.
     * @param {number} [options.height] - Desired image height in pixels.
     * @param {string} [options.crop] - Crop mode (e.g. 'center', 'left', 'right').
     * @returns {string} The URL with appended sizing parameters.
     */
    imageUrl(url, { width, height, crop } = {}) {
      if (!url) return '';
      const u = new URL(url, location.origin);
      if (width) u.searchParams.set('width', String(width));
      if (height) u.searchParams.set('height', String(height));
      if (crop) u.searchParams.set('crop', crop);
      return u.toString();
    }

    highlightMoco = (evt, element) => {
      clearTimeout(this.hoverTimer);
      this.reduceElementsOpacity(true);

      this.hoverTimer = setTimeout(() => {
        this.updateDom(element);
        this.handleMocoHoverGallery(element, evt);
        this.reduceElementsOpacity(false);
      }, 300);
    }

    updateDom = (element) => {
      const mocoData = {
        frame: element.dataset.frame,
        lens: element.dataset.lens,
      }

      if (this.productDom.lens) this.productDom.lens.textContent = mocoData.lens;
      if (this.productDom.frame) this.productDom.frame.textContent = mocoData.frame;
      this.updateBadges(element);
    }

    /**
     * Toggles the polarized badge visibility based on the element's data attribute.
     * @param {HTMLElement} element - The MOCO element being hovered/selected.
     */
    updateBadges(element) {
      if (!this.productDom.polarizedBadge) return;
      const isPolarized = element.dataset.polarized === 'true';
      this.productDom.polarizedBadge.classList.toggle('tw-hidden', !isPolarized);
    }

    /** Swaps a large preview in `[data-moco-gallery-hover]` outside this component. */
    handleMocoHoverGallery(element, evt) {
      if (this.galleryHover) {
        const mocoHandle = element.dataset.mocoProductHandle;

        if (evt.type === "mouseleave") {
          this.fadeOutGallery();
          return;
        }

        if (evt.type === "mouseenter") {
          this.fadeInGallery(mocoHandle, element);
        }
      }
    }

    fadeOutGallery() {
      const images = this.galleryHover.querySelectorAll('[data-moco-product]');
      images.forEach(img => img.classList.add('tw-opacity-0')); // fade out all

      // after fade completes
      setTimeout(() => {
        this.galleryHover.classList.remove('is-active');
      }, 300); // match tailwind tw-duration-300
    }

    fadeInGallery(mocoHandle, element) {
      this.galleryHover.classList.add('is-active');

      let imageEl = this.galleryHover.querySelector(`[data-moco-product="${mocoHandle}"]`);

      if (!imageEl && this.mocoHoverTemplate) {
        const { hoverSrc, hoverAlt } = element.dataset;
        if (hoverSrc) {
          const clone = this.mocoHoverTemplate.content.cloneNode(true);
          const wrapper = clone.querySelector('[data-moco-product]');
          wrapper.dataset.mocoProduct = mocoHandle;
          const img = clone.querySelector('[data-moco-hover-img]');
          img.src = hoverSrc;
          img.alt = hoverAlt || '';
          this.galleryHover.appendChild(clone);
          imageEl = this.galleryHover.querySelector(`[data-moco-product="${mocoHandle}"]`);
        }
      }

      if (imageEl) {
        // fade in current image
        requestAnimationFrame(() => {
          imageEl.classList.remove('tw-opacity-0');
        });

        // fade out others
        this.galleryHover.querySelectorAll('[data-moco-product]').forEach(img => {
          if (img !== imageEl) img.classList.add('tw-opacity-0');
        });
      }
    }

    reduceElementsOpacity(boolean) {
      Object.keys(this.productDom).forEach(key => {
        if (this.productDom[key]) this.productDom[key].classList.toggle('tw-opacity-50', boolean);
      });
    }
  }
  customElements.define("po-moco-selector", PoMocoSelector);
}